/**
 * Jetons de la v2 (docs/ux-v2/SPEC.md § 6.4) : la v2 se peint avec des variables CSS
 * `--mmv2-*` posées sur #mindbody-app[data-ux='v2']. Chaque jeton prend, dans l'ordre :
 *   1. le jeton du thème du site (`--m-*`, thème monday-2026), s'il existe ;
 *   2. le réglage du plugin (Réglages → Monday Mindbody → Personalisation) : sites Uncode ;
 *   3. la valeur de la maquette /msc (ou de la spec) quand rien n'est réglé.
 * Exception, la couleur d'action : le réglage « Couleur d'action (nouvelle interface) », quand il
 * est renseigné, passe avant le thème (sinon un réglage du BO resterait sans effet sur les sites
 * monday-2026) ; vide, on retombe sur l'ordre ci-dessus.
 *
 * Texte posé sur une couleur de marque ou d'action (boutons, spot choisi, cases cochées) : convention des marques,
 * garantie AA (lib/color.ts textOnColor, SPEC § 6.4) : blanc, comme sur les sites de Dynamo, RIISE et Punch, et la
 * surface foncée au minimum dans sa teinte quand le blanc n'y atteint pas 4,5:1 (jetons *Surface) ; la couleur
 * elle-même (liserés, pastilles, bordures) reste celle de la marque. Textes d'état : ajustés pour 4,5:1.
 *
 * Aucun effet de bord au chargement du module : tout est calculé par resolveTokens(), appelée
 * seulement quand la v2 est active.
 */
import { DEFAULT_BRAND_COLOR } from 'utils/brandColor';
import { AA, WHITE, ensureContrast, hslOf, hueDistance, mix, normalizeHex, textOnColor } from '../lib/color';

/** Valeurs de la maquette /msc (`<style>` de _ref/msc/index.html) et de la spec § 6.4. */
export const MOCKUP = {
  ink: '#0b0c11',
  ink2: '#454854',
  /** SPEC § 6.4 : #6b6e76 (4,57:1 sur le canvas) plutôt que le #82859A de la maquette (3,26:1). */
  ink3: '#6b6e76',
  ink4: '#b4b7c6',
  paper: '#ffffff',
  canvas: '#f2f2f6',
  sunken: '#e9e9f0',
  line: 'rgba(11, 12, 17, 0.1)',
  line2: 'rgba(11, 12, 17, 0.055)',
  green: '#0e9f6e',
  green50: '#e8f8f1',
  amber: '#b7791f',
  amber50: '#fff6e6',
  red: '#d92d20',
  red50: '#fef0ef',
  shadow1: '0 1px 2px rgba(11, 12, 17, 0.05), 0 4px 14px -6px rgba(11, 12, 17, 0.14)',
  shadow2: '0 2px 6px rgba(11, 12, 17, 0.06), 0 22px 48px -22px rgba(11, 12, 17, 0.34)',
  ease: 'cubic-bezier(0.16, 1, 0.3, 1)',
  /** Repli des rayons quand ni le thème ni le réglage ne les donnent (SPEC § 6.4). */
  rCard: '12px',
  rPill: '999px',
  rInput: '10px',
  rTag: '5px',
  /**
   * Teintes claires de la maquette, en part de la couleur mélangée au blanc (mesurées sur ses
   * paires : --blue-50 ≈ 5,5 %, --blue-100 ≈ 10,5 %, --orange-50 ≈ 8 %, bord de .notice.warm ≈ 21 %).
   */
  tint50: 0.055,
  tint100: 0.105,
  actionTint: 0.08,
  actionTintBorder: 0.21,
  /** Survol des boutons : --blue-700 / --orange-700 ≈ 12 % de noir. */
  hoverShade: 0.12,
} as const;

export const SYSTEM_FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif";

/** Réglages du plugin lus par la v2 (window.mondayMindbody). */
export interface V2Settings {
  color?: string;
  actionColor?: string;
  /** Réglage « Couleur de sélection (nouvelle interface) » : '' ou absent = encre. */
  selectionColor?: string;
  buttonBorderRadius?: string | number;
  cardBorderRadius?: string | number;
  fontFamily?: string;
}

/** Lit un jeton CSS du thème (`--m-*`) ; '' s'il n'existe pas. */
export type ThemeTokenReader = (name: string) => string;

export const noThemeTokens: ThemeTokenReader = () => '';

export interface V2Tokens {
  /** Couleur de marque telle quelle : liserés, pastilles, bordures (sans texte posé dessus). */
  brand: string;
  /** Fond sous un texte : la marque, foncée au minimum si son texte (brandOn) n'y atteint pas 4,5:1. */
  brandSurface: string;
  /** Survol d'un fond brandSurface. */
  brandHover: string;
  brandText: string;
  /** Texte posé sur brandSurface (convention des marques : blanc ; encre sur une couleur claire). */
  brandOn: string;
  brand50: string;
  brand100: string;
  action: string;
  /** Fond sous un texte : l'action, foncée au minimum si son texte (actionOn) n'y atteint pas 4,5:1. */
  actionSurface: string;
  actionHover: string;
  actionText: string;
  actionOn: string;
  action50: string;
  actionBorder: string;
  /**
   * Choix en cours du panier (moyen de paiement choisi, sa radio, Apple / Google Pay choisi, champ au focus) : réglage
   * « Couleur de sélection » du site, sinon l'encre ; 3:1 au moins sur le papier (WCAG 1.4.11). Encre par défaut : sur
   * Dynamo, la marque est le rouge des erreurs (retours de Ségolène du 13/10).
   */
  select: string;
  ink: string;
  ink2: string;
  ink3: string;
  ink4: string;
  paper: string;
  canvas: string;
  sunken: string;
  line: string;
  line2: string;
  /** Bordure d'un champ au repos : 3:1 au moins sur le papier (WCAG 1.4.11), dans la teinte de ink4. */
  fieldBorder: string;
  green: string;
  greenText: string;
  green50: string;
  amber: string;
  amberText: string;
  amber50: string;
  red: string;
  redText: string;
  /** Survol d'un bouton rouge (danger) : visible quand le rouge est la couleur d'action (Dynamo). */
  redHover: string;
  red50: string;
  font: string;
  fontDisplay: string;
  /**
   * Espace entre mots des titres en police d'affichage : 0,1em pour la police condensée du thème (Archivo Cond, espaces
   * resserrés par l'interlettrage négatif), « normal » sans police d'affichage (titres dans la police du texte, Eastman
   * des sites Uncode : « ANNULER  MA  RÉSERVATION » trop espacé, revue du 05/10).
   */
  displayWordSpacing: string;
  rCard: string;
  rPill: string;
  rChip: string;
  rInput: string;
  rTag: string;
  shadow1: string;
  shadow2: string;
  ease: string;
  focus: string;
}

/** Réglages du plugin injectés par Frontend.php (customization + uxV2). */
export function readSettings(): V2Settings {
  if (typeof mondayMindbody === 'undefined' || !mondayMindbody) return {};
  const c = mondayMindbody.customization ?? ({} as typeof mondayMindbody.customization);
  return {
    color: c?.color,
    actionColor: mondayMindbody.uxV2?.actionColor,
    selectionColor: mondayMindbody.uxV2?.selectionColor,
    buttonBorderRadius: c?.buttonBorderRadius,
    cardBorderRadius: c?.cardBorderRadius,
    fontFamily: c?.fontFamily,
  };
}

/** Lecteur des jetons `--m-*` posés par le thème sur la racine du document. */
export function cssThemeTokenReader(root: Element = document.documentElement): ThemeTokenReader {
  const style = window.getComputedStyle(root);
  return (name) => style.getPropertyValue(name).trim();
}

/** '20' / 20 / '20px' → '20px' ; '0' → '0px' ; vide ou illisible → ''. */
export function toPx(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  const m = /^(\d+(?:\.\d+)?)(px)?$/.exec(text);
  return m ? `${m[1]}px` : '';
}

/** Pile de polices à partir du réglage « Police » (même règle que --cv2-font du Nouveau Panier). */
export function fontStackFromSetting(fontFamily: string | undefined): string {
  const name = (fontFamily ?? '').replace(/['"]/g, '').trim();
  return name ? `'${name}', 'Helvetica Neue', Helvetica, Arial, sans-serif` : '';
}

const firstColor = (...candidates: Array<string | undefined | null>): string | null => {
  for (const candidate of candidates) {
    const hex = normalizeHex(candidate);
    if (hex) return hex;
  }
  return null;
};

const firstValue = (...candidates: Array<string | undefined | null>): string => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
  }
  return '';
};

/**
 * Couleur d'action qui EST un rouge franc (Dynamo : #E0141C, à 6,6° du rouge d'erreur de la maquette) : le rouge
 * d'erreur (boutons d'annulation, encadrés et étiquettes d'erreur) prend alors ses valeurs, sinon deux rouges voisins
 * se côtoient dans une même modale (revue du 05/10 : #D92D20 contre #E0141C et #D9131B). Orange de Punch (12°), brun
 * de RIISE (sombre) : rouge d'erreur de la maquette, distinct de l'action.
 */
export function actionIsDangerRed(action: string): boolean {
  const hsl = hslOf(action);
  return !!hsl && hsl.s >= 0.6 && hsl.l >= 0.35 && hsl.l <= 0.65 && hueDistance(action, MOCKUP.red) <= 8;
}

/** Calcule les jetons v2 d'un site. Pure : réglages et lecteur du thème sont passés en entrée. */
export function resolveTokens(settings: V2Settings, theme: ThemeTokenReader = noThemeTokens): V2Tokens {
  const themeBrand = normalizeHex(theme('--m-brand-700'));
  const settingColor = normalizeHex(settings.color);

  const brand = themeBrand ?? settingColor ?? DEFAULT_BRAND_COLOR;

  const actionSetting = normalizeHex(settings.actionColor);
  const action = actionSetting ?? themeBrand ?? settingColor ?? brand;

  const ink = firstColor(theme('--m-ink')) ?? MOCKUP.ink;
  const brandOnColor = textOnColor(brand, ink, WHITE);
  const actionOnColor = textOnColor(action, ink, WHITE);
  // Survol : jeton --m-brand-800 du thème quand la surface est sa couleur de marque telle quelle, sinon la surface
  // assombrie (12 % de noir, comme la maquette).
  const themeHover = normalizeHex(theme('--m-brand-800'));
  const hoverOf = (surface: string, fromTheme: boolean) =>
    fromTheme && themeHover && surface === themeBrand ? themeHover : mix('#000000', surface, MOCKUP.hoverShade);
  const actionHover = hoverOf(actionOnColor.surface, !actionSetting);

  const paper = firstColor(theme('--m-paper')) ?? MOCKUP.paper;
  const brand100 = mix(brand, WHITE, MOCKUP.tint100);
  const action50 = mix(action, WHITE, MOCKUP.actionTint);

  const actionText = ensureContrast(ensureContrast(action, paper, AA), action50, AA);
  const dangerFollowsAction = actionIsDangerRed(action);

  const font = firstValue(theme('--m-font-body'), fontStackFromSetting(settings.fontFamily), SYSTEM_FONT_STACK);
  const rPill = firstValue(theme('--m-r-pill'), toPx(settings.buttonBorderRadius), MOCKUP.rPill);

  return {
    brand,
    brandSurface: brandOnColor.surface,
    brandHover: hoverOf(brandOnColor.surface, true),
    // Texte à la couleur de marque (< 24 px) sur fond clair (blanc ou teinte -100) : jeton du thème,
    // sinon marque assombrie juste assez pour 4,5:1.
    brandText: firstColor(theme('--m-brand-text')) ?? ensureContrast(ensureContrast(brand, paper, AA), brand100, AA),
    brandOn: brandOnColor.text,
    brand50: mix(brand, WHITE, MOCKUP.tint50),
    brand100,
    action,
    actionSurface: actionOnColor.surface,
    actionHover,
    // Texte à la couleur d'action sur blanc ou sur sa teinte -50 (étiquettes, liens d'action).
    actionText,
    actionOn: actionOnColor.text,
    action50,
    actionBorder: mix(action, WHITE, MOCKUP.actionTintBorder),
    select: ensureContrast(normalizeHex(settings.selectionColor) ?? ink, paper, 3),
    ink,
    ink2: firstValue(theme('--m-ink-2'), MOCKUP.ink2),
    ink3: firstValue(theme('--m-ink-3'), MOCKUP.ink3),
    ink4: firstValue(theme('--m-ink-4'), MOCKUP.ink4),
    paper,
    canvas: firstValue(theme('--m-canvas'), MOCKUP.canvas),
    sunken: firstValue(theme('--m-sunken'), MOCKUP.sunken),
    line: firstValue(theme('--m-line'), MOCKUP.line),
    line2: firstValue(theme('--m-line-2'), MOCKUP.line2),
    fieldBorder: ensureContrast(firstColor(theme('--m-ink-4')) ?? MOCKUP.ink4, paper, 3),
    // États : couleurs de la maquette (pastilles, liserés) ; leurs textes, ajustés pour 4,5:1 sur leur fond clair.
    green: MOCKUP.green,
    greenText: ensureContrast(MOCKUP.green, MOCKUP.green50, AA),
    green50: MOCKUP.green50,
    amber: MOCKUP.amber,
    amberText: ensureContrast(MOCKUP.amber, MOCKUP.amber50, AA),
    amber50: MOCKUP.amber50,
    red: dangerFollowsAction ? actionOnColor.surface : MOCKUP.red,
    redText: dangerFollowsAction ? actionText : ensureContrast(MOCKUP.red, MOCKUP.red50, AA),
    // Rouge = action (Dynamo) : survol de l'action (#E0141C → #BB0F16) ; redText n'en diffère pas assez (1,05:1, revue
    // du 05/10). Sinon, le rouge de texte, plus foncé, comme avant.
    redHover: dangerFollowsAction ? actionHover : ensureContrast(MOCKUP.red, MOCKUP.red50, AA),
    red50: dangerFollowsAction ? action50 : MOCKUP.red50,
    font,
    fontDisplay: firstValue(theme('--m-font-display'), font),
    displayWordSpacing: firstValue(theme('--m-font-display'), '') ? '0.1em' : 'normal',
    rCard: firstValue(theme('--m-r-card'), toPx(settings.cardBorderRadius), MOCKUP.rCard),
    rPill,
    rChip: firstValue(theme('--m-r-chip'), rPill),
    rInput: firstValue(theme('--m-r-input'), toPx(settings.cardBorderRadius), MOCKUP.rInput),
    rTag: firstValue(theme('--m-r-tag'), MOCKUP.rTag),
    shadow1: firstValue(theme('--m-shadow-1'), MOCKUP.shadow1),
    shadow2: firstValue(theme('--m-shadow-2'), MOCKUP.shadow2),
    ease: firstValue(theme('--m-ease'), MOCKUP.ease),
    focus: firstColor(theme('--m-focus')) ?? action,
  };
}

const CSS_VAR_NAMES: Record<keyof V2Tokens, string> = {
  brand: '--mmv2-brand',
  brandSurface: '--mmv2-brand-surface',
  brandHover: '--mmv2-brand-hover',
  brandText: '--mmv2-brand-text',
  brandOn: '--mmv2-brand-on',
  brand50: '--mmv2-brand-50',
  brand100: '--mmv2-brand-100',
  action: '--mmv2-action',
  actionSurface: '--mmv2-action-surface',
  actionHover: '--mmv2-action-hover',
  actionText: '--mmv2-action-text',
  actionOn: '--mmv2-action-on',
  action50: '--mmv2-action-50',
  actionBorder: '--mmv2-action-border',
  select: '--mmv2-select',
  ink: '--mmv2-ink',
  ink2: '--mmv2-ink-2',
  ink3: '--mmv2-ink-3',
  ink4: '--mmv2-ink-4',
  paper: '--mmv2-paper',
  canvas: '--mmv2-canvas',
  sunken: '--mmv2-sunken',
  line: '--mmv2-line',
  line2: '--mmv2-line-2',
  fieldBorder: '--mmv2-field-border',
  green: '--mmv2-green',
  greenText: '--mmv2-green-text',
  green50: '--mmv2-green-50',
  amber: '--mmv2-amber',
  amberText: '--mmv2-amber-text',
  amber50: '--mmv2-amber-50',
  red: '--mmv2-red',
  redText: '--mmv2-red-text',
  redHover: '--mmv2-red-hover',
  red50: '--mmv2-red-50',
  font: '--mmv2-font',
  fontDisplay: '--mmv2-font-display',
  displayWordSpacing: '--mmv2-display-ws',
  rCard: '--mmv2-r-card',
  rPill: '--mmv2-r-pill',
  rChip: '--mmv2-r-chip',
  rInput: '--mmv2-r-input',
  rTag: '--mmv2-r-tag',
  shadow1: '--mmv2-shadow-1',
  shadow2: '--mmv2-shadow-2',
  ease: '--mmv2-ease',
  focus: '--mmv2-focus',
};

/** Jetons → variables CSS `--mmv2-*` (posées sur #mindbody-app par mountV2). */
export function toCssVars(tokens: V2Tokens): Record<string, string> {
  const vars: Record<string, string> = {};
  (Object.keys(CSS_VAR_NAMES) as Array<keyof V2Tokens>).forEach((key) => {
    vars[CSS_VAR_NAMES[key]] = tokens[key];
  });
  return vars;
}
